import { dateFiltersForApi } from '../lib/dateRangeFilters';
import type { VoiceContext, VoiceTranscript } from '../lib/voiceTranscript';
import type { BoardDocument, BoardResponse } from '../features/board/boardModel';
import type {
  ApiEnvelope,
  Asset,
  AssetDescription,
  AssetFilters,
  Attachment,
  FileListing,
  Regulation,
  RegulationStats,
  State,
  StateFilters,
  StateUpdate,
  User,
} from './types';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

export function voiceTranscript(url: string, context: VoiceContext, start = false, signal?: AbortSignal) {
  const data = { url, ...context };
  return start
    ? request<VoiceTranscript>('/api/attachments/voice-transcript', { method: 'POST', body: data, signal })
    : request<VoiceTranscript>('/api/attachments/voice-transcript?' + new URLSearchParams(Object.entries(data).map(([key, value]) => [key, String(value)])).toString(), { signal });
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers = new Headers(options.headers);
  if (options.method && !['GET', 'HEAD'].includes(options.method)) headers.set('X-Skrepka-Request', '1');
  let body: BodyInit | undefined;

  if (options.body instanceof FormData || options.body instanceof Blob || typeof options.body === 'string') {
    body = options.body;
  } else if (options.body !== undefined) {
    headers.set('Content-Type', 'application/json');
    body = JSON.stringify(options.body);
  }

  let response: Response;
  try {
    response = await fetch(path, {
    ...options,
    body,
    headers,
    credentials: 'same-origin',
  });
  } catch {
    throw new ApiError('Нет связи с сервером. Проверьте интернет и повторите сохранение.', 0);
  }

  const hasJson = response.headers.get('content-type')?.includes('application/json');
  let payload: unknown;
  if (hasJson) {
    try {
      payload = await response.json();
    } catch {
      payload = undefined;
    }
  }

  if (!response.ok) {
    const details = (payload as ApiEnvelope<unknown> | undefined)?.data;
    const errorMessage = response.status === 413
      ? 'Сервер отклонил загрузку: превышено ограничение на размер запроса.'
      : typeof details === 'string'
        ? details
        : typeof details === 'object' && details !== null && 'error' in details
          ? String(details.error)
          : `HTTP ${response.status}`;
    throw new ApiError(errorMessage, response.status, details);
  }

  return (payload as ApiEnvelope<T> | undefined)?.data as T;
}

function params(values: Record<string, string | number | boolean | undefined>): string {
  const query = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined) query.set(key, String(value));
  });
  const encoded = query.toString();
  return encoded ? `?${encoded}` : '';
}

export const api = {
  projectBoard: (id: number) => request<BoardResponse>(`/api/projects/${id}/board`),
  saveProjectBoard: (id: number, document: BoardDocument, expectedRevision: number) => request<BoardResponse>(`/api/projects/${id}/board`, { method: 'PUT', body: { document, expectedRevision } }),
  assetBoard: (id: number) => request<BoardResponse>(`/api/assets/${id}/board`),
  saveAssetBoard: (id: number, document: BoardDocument, expectedRevision: number) => request<BoardResponse>(`/api/assets/${id}/board`, { method: 'PUT', body: { document, expectedRevision } }),
  currentUser: () => request<User>('/api/user'),
  login: async (login: string, password: string) => {
    const result = await request<User | [string, number]>('/api/user/login', {
      method: 'POST',
      body: { login, password },
    });
    // Some deployments return the historical `(message, 401)` tuple inside
    // a 200 JSON envelope. Treat it as the authentication error it represents.
    if (Array.isArray(result)) throw new ApiError(result[0], result[1], result);
    return result;
  },
  logout: () => request<void>('/api/user/logout', { method: 'POST', body: {} }),
  chatUsers: () => request<User[]>('/api/chat/users?include_inactive=true'),
  users: (options: { projectId?: number; addInfo?: boolean; includeInactive?: boolean; scope?: 'tasks' } = {}) =>
    request<User[]>(
      `/api/users${params({
        project_id: options.projectId,
        add_info: options.addInfo,
        include_inactive: options.includeInactive,
        scope: options.scope,
      })}`,
    ),
  regulation: (id: number) => request<Regulation>(`/api/regulation/${id}`),
  regulationAssets: (id: number) =>
    request<{ title: string; assets: Array<{ id: number; title: string }> }>(
      `/api/regulation/${id}/asset`,
    ),
  states: (filters: StateFilters) =>
    request<State[]>('/api/states/data', { method: 'POST', body: dateFiltersForApi(filters) }),
  regulationStats: (stepId: number) =>
    request<RegulationStats>('/api/states/regulation_stats', {
      method: 'POST',
      body: { reg_step_id: stepId },
    }),
  state: (id: number) => request<State>(`/api/states/${id}?next_steps=true`),
  updateState: (id: number, update: StateUpdate) =>
    request<State>(`/api/states/${id}`, { method: 'PUT', body: update }),
  updateStates: (ids: number[], update: StateUpdate & { attachment_ids?: number[] }) =>
    request<Record<string, State>>('/api/states/update_multiple', {
      method: 'POST',
      body: { ...update, ids },
    }),
  reorderStates: (ids: number[], positions: number[]) =>
    request<Record<string, State>>('/api/states/update_multiple', {
      method: 'POST',
      body: { ids, positions },
    }),
  assets: (regulationId: number, filters: AssetFilters) =>
    request<Asset[]>(`/api/assets/data/${regulationId}?view=grid`, { method: 'POST', body: {
      ...filters,
      ...(filters.steps ? { steps: Object.fromEntries(Object.entries(filters.steps).map(([id, step]) => [id, dateFiltersForApi(step)])) } : {}),
    } }),
  findAssets: (title: string) =>
    request<Array<{ id: number; title: string }>>(`/api/assets/find${params({ title })}`),
  asset: (id: number) => request<Asset>(`/api/assets/${id}`),
  assetTitle: (id: number) => request<{ title: string }>(`/api/assets/${id}/title`),
  assetChildren: (id: number) =>
    request<{
      data: Array<{
        regulation: Regulation;
        project: { id: number; title: string };
        assets: number[];
      }>;
    }>(`/api/assets/${id}/childs`),
  assetDescription: (id: number) => request<AssetDescription>(`/api/assets/${id}/description`),
  updateAssetDescription: (id: number, text: string) =>
    request<void>(`/api/assets/${id}/description`, { method: 'PUT', body: { text } }),
  files: (assetId: number, pathName: string, relativePath = '') => {
    const encodedPath = relativePath
      .split('/')
      .filter(Boolean)
      .map(encodeURIComponent)
      .join('/');
    return request<FileListing>(
      `/api/assets/files/${assetId}/${encodeURIComponent(pathName)}${encodedPath ? `/${encodedPath}` : ''}`,
    );
  },
  pendingAttachments: (stateId: number) =>
    request<Attachment[]>(`/api/attachments/?state_id=${stateId}`),
  deleteAttachment: (url: string) => request<void>(url, { method: 'DELETE' }),
  // Chunked like every other upload, otherwise a photo taken on a phone does
  // not fit into a single request.
  uploadAvatar: (file: File) => uploadAttachment(file),
  setAvatar: (attachmentId: number) =>
    request<void>(`/api/attachments/${attachmentId}/set_as_avatar`, { method: 'POST' }),
};

// The legacy flow.js uploader cut files into 2 MB chunks, which is why the
// nginx body limit never got in the way. Do the same again: every request
// stays small and FileStorage.upload puts the chunks back together.
const UPLOAD_CHUNK_SIZE = 2 * 1024 * 1024;

/** Attachment size ceiling agreed with the users. */
export const MAX_UPLOAD_SIZE = 100 * 1024 * 1024;
export const MAX_UPLOAD_SIZE_LABEL = '100 МБ';

async function retryVideoStep<T>(operation: () => Promise<T>, onStatus?: (text: string) => void): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try { return await operation(); }
    catch (error) {
      const permanent = error instanceof ApiError && error.status >= 400 && error.status < 500 && ![408, 429].includes(error.status);
      if (attempt >= 4 || permanent) throw error;
      onStatus?.('Связь прервалась. Повторяем загрузку…');
      await new Promise(resolve => setTimeout(resolve, 1000 * 2 ** attempt));
    }
  }
}

async function uploadReviewVideo(file: File, stateId?: number, onProgress?: (ratio: number) => void, onStatus?: (text: string) => void): Promise<Attachment> {
  const started = await request<{ uploadId: string }>('/api/attachments/video-upload/start', {
    method: 'POST', body: { name: file.name, size: file.size, stateId },
  });
  if (!started?.uploadId) throw new ApiError('Сервер не начал загрузку видео.', 500);
  const path = `/api/attachments/video-upload/${encodeURIComponent(started.uploadId)}`;
  try {
    for (let offset = 0; offset < file.size; offset += UPLOAD_CHUNK_SIZE) {
      const end = Math.min(file.size, offset + UPLOAD_CHUNK_SIZE);
      onStatus?.(`Загрузка видео · ${Math.round(offset / file.size * 100)}%`);
      // Capacitor serializes a plain Blob as JSON; File uses its binary transport.
      await retryVideoStep(async () => {
        const response = await request<{ received: number }>(`${path}?offset=${offset}`, { method: 'PUT', headers: { 'Content-Type': 'application/octet-stream' }, body: new File([file.slice(offset, end)], file.name, { type: 'application/octet-stream' }) });
        if (!response || !Number.isSafeInteger(response.received) || response.received < end) throw new ApiError('Сервер не подтвердил фрагмент видео.', 500);
      }, onStatus);
      onProgress?.(end / file.size * .7);
    }
    onStatus?.('Готовим просмотровую версию…');
    for (;;) {
      const result = await retryVideoStep(() => request<{ status: string; progress?: number; message?: string; attachment?: Attachment }>(path, { method: 'POST', body: {} }), onStatus);
      if (result?.status === 'complete' && result.attachment) { onProgress?.(1); onStatus?.(''); return result.attachment; }
      if (result?.status === 'error') throw new ApiError(result.message || 'Не удалось подготовить видео.', 422);
      if (!['queued', 'processing', 'busy'].includes(result?.status)) throw new ApiError('Сервер не подтвердил подготовку видео.', 500);
      onStatus?.(result.status === 'processing' ? `Готовим просмотровую версию · ${result.progress || 0}%` : 'Видео ожидает обработки…');
      onProgress?.(.7 + Math.min(99, result.progress || 0) / 100 * .29);
      await new Promise(resolve => setTimeout(resolve, 1200));
    }
  } catch (error) {
    void request(path, { method: 'DELETE' }).catch(() => undefined);
    throw error;
  }
}

export async function uploadVoice(blob: Blob): Promise<Attachment> {
  return request<Attachment>('/api/attachments/voice-upload', { method: 'POST', body: blob });
}

export async function uploadAttachment(
  file: File,
  options: { stateId?: number; assetId?: number; pathName?: string; relativePath?: string } = {},
  onProgress?: (ratio: number) => void,
  onStatus?: (text: string) => void,
): Promise<Attachment> {
  // MP3 attachments are original files, not microphone recordings. Keep their
  // names and bytes and use the bounded chunk transport, including long audio.
  const mp3 = /\.mp3$/i.test(file.name) || file.type === 'audio/mpeg';
  if (!mp3 && file.type.startsWith('audio/') && options.stateId === undefined) return uploadVoice(file);
  const video = !file.type.startsWith('audio/') && (file.type.startsWith('video/') || /\.(mov|mp4|m4v|mxf|webm|mkv|avi|flv)$/i.test(file.name));
  if (video && options.assetId === undefined) return uploadReviewVideo(file, options.stateId, onProgress, onStatus);
  onStatus?.('');
  if (file.size > MAX_UPLOAD_SIZE) {
    throw new ApiError(`«${file.name}» больше ${MAX_UPLOAD_SIZE_LABEL} — такой файл загрузить нельзя.`, 413);
  }
  const identifier = `${file.size}-${file.lastModified}-${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
  const totalChunks = Math.max(1, Math.ceil(file.size / UPLOAD_CHUNK_SIZE));
  let attachment: Attachment | undefined;

  // Chunks go up strictly in order: the backend counts them in redis and
  // assembles the file on the last one, so they cannot be parallelized.
  for (let chunkNumber = 1; chunkNumber <= totalChunks; chunkNumber += 1) {
    const start = (chunkNumber - 1) * UPLOAD_CHUNK_SIZE;
    const end = Math.min(start + UPLOAD_CHUNK_SIZE, file.size);
    const form = new FormData();
    form.append('file', file.slice(start, end, file.type), file.name);
    form.append('flowChunkNumber', String(chunkNumber));
    form.append('flowChunkSize', String(UPLOAD_CHUNK_SIZE));
    form.append('flowCurrentChunkSize', String(end - start));
    form.append('flowTotalSize', String(file.size));
    form.append('flowIdentifier', identifier);
    form.append('flowFilename', file.name);
    form.append('flowRelativePath', file.name);
    form.append('flowTotalChunks', String(totalChunks));
    if (options.stateId !== undefined) form.append('state_id', String(options.stateId));
    if (options.assetId !== undefined) form.append('asset_id', String(options.assetId));
    if (options.pathName !== undefined) form.append('path_name', options.pathName);
    if (options.relativePath !== undefined) form.append('rel_path', options.relativePath);
    // Only the response to the final chunk carries the attachment; the
    // earlier ones answer with a plain "OK".
    const uploaded = await request<Attachment | undefined>('/api/attachments/upload', { method: 'POST', body: form });
    if (uploaded) attachment = uploaded;
    onProgress?.(file.size ? end / file.size : 1);
  }

  if (!attachment) throw new ApiError('Сервер не вернул загруженный файл', 500);
  return attachment;
}

export function fileUrl(assetId: number, pathName: string, relativePath: string, download = false) {
  const encodedPath = relativePath
    .split('/')
    .filter(Boolean)
    .map(encodeURIComponent)
    .join('/');
  const suffix = download ? '?download_even_folder=true' : '';
  return `/api/assets/files/${assetId}/${encodeURIComponent(pathName)}/${encodedPath}${suffix}`;
}

export function previewUrl(assetId: number, pathName: string, relativePath: string, width = 240, height = 160) {
  const encodedPath = relativePath
    .split('/')
    .filter(Boolean)
    .map(encodeURIComponent)
    .join('/');
  return `/api/assets/files/preview/${assetId}/${encodeURIComponent(pathName)}/${encodedPath}?w=${width}&h=${height}`;
}
